/**
 * Trade text generation utilities for MJJ Trading Tool
 * Requirements: 8.1, 8.4
 */

import type { CardData, CalculationResult, CategoryType } from '../types/card';

/**
 * 取得商品類型的中文名稱
 */
function getCategoryLabel(category: CategoryType): string {
  const labels: Record<CategoryType, string> = {
    vps: 'VPS',
    vds: 'VDS/獨服',
    hosting: '虛擬主機',
    account: '帳號/會員',
    domain: '域名',
  };
  return labels[category] || category;
}

/**
 * 格式化硬碟配置
 */
function formatDisks(cardData: CardData): string {
  return cardData.disks
    .map((disk) => `${disk.size}${disk.unit} ${disk.type}`)
    .join(' + ');
}

/**
 * 格式化流量配置
 */
function formatTraffic(cardData: CardData): string {
  if (cardData.traffic.isUnlimited) {
    return '無限流量';
  }
  return `${cardData.traffic.value}${cardData.traffic.unit} (${cardData.traffic.direction})`;
}

/**
 * 格式化網路速度
 */
function formatSpeed(cardData: CardData): string {
  return `${cardData.traffic.speed}${cardData.traffic.speedUnit}`;
}

/**
 * 格式化價格
 */
function formatPrice(price: number, currency: string): string {
  return `${currency} ${price.toFixed(2)}`;
}

/**
 * 生成伺服器類型的文案
 */
function generateServerText(cardData: CardData, result: CalculationResult): string {
  const lines: string[] = [];

  // 標題
  lines.push(`【${getCategoryLabel(cardData.category)}出售】${cardData.provider} - ${cardData.productName}`);
  lines.push('');

  // 基本資訊
  lines.push(`📍 地區：${cardData.region}`);
  lines.push(`🔗 線路：${cardData.routeType === 'custom' ? cardData.customRoute : cardData.routeType}`);
  lines.push('');

  // 規格
  lines.push('📦 規格：');
  lines.push(`  • CPU：${cardData.cpu}`);
  lines.push(`  • RAM：${cardData.ram}`);
  lines.push(`  • 硬碟：${formatDisks(cardData)}`);
  lines.push(`  • 流量：${formatTraffic(cardData)}`);
  lines.push(`  • 端口：${formatSpeed(cardData)}`);
  if (cardData.traffic.throttleInfo) {
    lines.push(`  ⚠️ ${cardData.traffic.throttleInfo}`);
  }
  lines.push('');

  // 價格資訊
  lines.push('💰 價格：');
  lines.push(`  • 續費價：${formatPrice(cardData.renewalPrice, cardData.currency)}`);
  lines.push(`  • 剩餘天數：${result.remainingDays} 天`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • 剩餘價值：${formatPrice(result.residualValue, cardData.currency)}`);
  }
  lines.push(`  • 售價：${formatPrice(cardData.sellingPrice, cardData.currency)} (${cardData.targetCurrency} ${result.sellingPriceTarget})`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • ${result.isPremium ? '溢價' : '折價'}：${formatPrice(result.premium, cardData.currency)}`);
  }
  lines.push('');

  // 備註
  if (cardData.remarks) {
    lines.push(`📝 備註：${cardData.remarks}`);
    lines.push('');
  }

  // 聯繫方式
  lines.push('📞 聯繫方式：');
  if (cardData.contact.tg) {
    lines.push(`  • Telegram：${cardData.contact.tg}`);
  }
  if (cardData.contact.xy) {
    lines.push(`  • 交易平台：${cardData.contact.xy}`);
  }

  return lines.join('\n');
}

/**
 * 生成域名類型的文案
 */
function generateDomainText(cardData: CardData, result: CalculationResult): string {
  const lines: string[] = [];

  // 標題
  lines.push(`【域名出售】${cardData.productName}`);
  lines.push('');

  // 基本資訊
  lines.push(`🏢 註冊商：${cardData.provider}`);
  lines.push('');

  // 價格資訊
  lines.push('💰 價格：');
  lines.push(`  • 續費價：${formatPrice(cardData.renewalPrice, cardData.currency)}`);
  lines.push(`  • 剩餘天數：${result.remainingDays} 天`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • 剩餘價值：${formatPrice(result.residualValue, cardData.currency)}`);
  }
  lines.push(`  • 售價：${formatPrice(cardData.sellingPrice, cardData.currency)} (${cardData.targetCurrency} ${result.sellingPriceTarget})`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • ${result.isPremium ? '溢價' : '折價'}：${formatPrice(result.premium, cardData.currency)}`);
  }
  lines.push('');

  // 備註
  if (cardData.remarks) {
    lines.push(`📝 備註：${cardData.remarks}`);
    lines.push('');
  }

  // 聯繫方式
  lines.push('📞 聯繫方式：');
  if (cardData.contact.tg) {
    lines.push(`  • Telegram：${cardData.contact.tg}`);
  }
  if (cardData.contact.xy) {
    lines.push(`  • 交易平台：${cardData.contact.xy}`);
  }

  return lines.join('\n');
}

/**
 * 生成帳號類型的文案
 */
function generateAccountText(cardData: CardData, result: CalculationResult): string {
  const lines: string[] = [];

  // 標題
  lines.push(`【帳號出售】${cardData.provider} - ${cardData.productName}`);
  lines.push('');

  // 價格資訊
  lines.push('💰 價格：');
  lines.push(`  • 續費價：${formatPrice(cardData.renewalPrice, cardData.currency)}`);
  lines.push(`  • 剩餘天數：${result.remainingDays} 天`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • 剩餘價值：${formatPrice(result.residualValue, cardData.currency)}`);
  }
  lines.push(`  • 售價：${formatPrice(cardData.sellingPrice, cardData.currency)} (${cardData.targetCurrency} ${result.sellingPriceTarget})`);
  if (cardData.billingCycle !== 0) {
    lines.push(`  • ${result.isPremium ? '溢價' : '折價'}：${formatPrice(result.premium, cardData.currency)}`);
  }
  lines.push('');

  // 備註
  if (cardData.remarks) {
    lines.push(`📝 備註：${cardData.remarks}`);
    lines.push('');
  }

  // 聯繫方式
  lines.push('📞 聯繫方式：');
  if (cardData.contact.tg) {
    lines.push(`  • Telegram：${cardData.contact.tg}`);
  }
  if (cardData.contact.xy) {
    lines.push(`  • 交易平台：${cardData.contact.xy}`);
  }

  return lines.join('\n');
}

/**
 * 生成交易文案
 * Property 6: 文案生成完整性
 * Validates: Requirements 8.1, 8.2, 8.4
 *
 * @param cardData - 卡片資料
 * @param result - 計算結果
 * @returns 格式化的交易文案
 */
export function generateTradeText(cardData: CardData, result: CalculationResult): string {
  switch (cardData.category) {
    case 'domain':
      return generateDomainText(cardData, result);
    case 'account':
      return generateAccountText(cardData, result);
    case 'vps':
    case 'vds':
    case 'hosting':
    default:
      return generateServerText(cardData, result);
  }
}
